import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Brand Colors
const colors = {
  primary: '#3B82F6',
  primaryHover: '#2563EB',
  lightAccent: '#DBEAFE',
  bgWhite: '#FFFFFF',
  bgLight: 'rgba(219, 234, 254, 0.08)',
  bgMedium: 'rgba(219, 234, 254, 0.12)',
  bgHero: 'rgba(219, 234, 254, 0.18)',
  text: '#0F172A',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  cardShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
};

// MealGuide Logo
const MealGuideLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_59057805-c3b3-4e46-9604-aba39f495103/artifacts/w3lnoxmz_Namnl%C3%B6s%20%28250%20x%20150%20px%29.png"
    alt="MealGuide"
    className="h-16 sm:h-20 w-auto object-contain"
  />
);

// Animation hook
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '20px' }
    );

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

// Animated wrapper
const Animated = ({ children, className = '', delay = 0, distance = 12 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-1000 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : `translateY(${distance}px)`,
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Navigate to signup/checkout page
const handleSubscribe = () => {
  window.location.href = '/signup';
};

// Smooth scroll with offset for fixed header
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80; // Account for fixed header
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Enhanced Phone Mockup Component with glow, shadow, and rotation options
const PhoneMockup = ({ 
  src, 
  alt, 
  className = '', 
  rotate = 0, 
  size = 'md',
  glowColor = 'rgba(59, 130, 246, 0.15)',
  hover = true 
}) => {
  const sizeClasses = {
    sm: 'w-[190px] sm:w-[220px] lg:w-[240px]',
    md: 'w-[220px] sm:w-[260px] lg:w-[280px]',
    lg: 'w-[240px] sm:w-[290px] lg:w-[370px]',
    xl: 'w-[280px] sm:w-[310px] lg:w-[360px]'
  };

  return (
    <div 
      className={`relative ${className} ${hover ? 'transition-transform duration-500 hover:scale-[1.02]' : ''}`}
      style={{ 
        transform: `rotate(${rotate}deg)`,
        transformOrigin: 'center center'
      }}
    >
      {/* Glow effect behind the phone */}
      <div 
        className="absolute inset-0 rounded-[3rem] blur-3xl opacity-60"
        style={{ 
          background: glowColor,
          transform: 'scale(0.85) translateY(10%)'
        }}
      />
      
      {/* Phone image with enhanced shadow */}
      <img 
        src={src} 
        alt={alt}
        className={`relative ${sizeClasses[size]} h-auto`}
        style={{
          filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.15)) drop-shadow(0 10px 20px rgba(0, 0, 0, 0.1))'
        }}
        loading="lazy"
      />
    </div>
  );
};

export default function LandingPage() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Close mobile menu when clicking outside or on a link
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === 'function') {
      action();
    }
  };

  return (
    <div 
      className="min-h-screen antialiased"
      style={{ 
        backgroundColor: colors.bgWhite, 
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' 
      }}
    >
      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md"
        style={{ 
          backgroundColor: 'rgba(255,255,255,0.92)',
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <button 
              onClick={scrollToTop}
              className="flex items-center focus:outline-none transition-all duration-200 hover:opacity-75"
            >
              <MealGuideLogo />
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <nav className="flex items-center gap-6">
                {['How it works', 'Testimonials', 'Pricing'].map((item) => (
                  <button 
                    key={item}
                    onClick={() => {
                      if (item === 'How it works') scrollToSection('how-it-works');
                      else if (item === 'Testimonials') scrollToSection('testimonials');
                      else scrollToSection('pricing');
                    }}
                    className="text-sm font-medium transition-all duration-200 hover:text-[#0F172A]"
                    style={{ color: colors.textSecondary }}
                  >
                    {item}
                  </button>
                ))}
              </nav>
              
              <button 
                onClick={() => navigate('/app/login')}
                className="text-sm font-medium transition-all duration-200 hover:text-[#0F172A]"
                style={{ color: colors.textSecondary }}
              >
                Log in
              </button>
              
              <button 
                onClick={() => navigate('/signup')}
                className="text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 hover:opacity-90"
                style={{ 
                  backgroundColor: colors.primary,
                  color: '#FFFFFF'
                }}
              >
                Sign up
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg transition-all duration-200 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X size={24} style={{ color: colors.text }} />
              ) : (
                <Menu size={24} style={{ color: colors.text }} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={`lg:hidden absolute top-full left-0 right-0 transition-all duration-300 ease-in-out ${
            mobileMenuOpen 
              ? 'opacity-100 translate-y-0 pointer-events-auto' 
              : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          style={{
            backgroundColor: 'rgba(255,255,255,0.98)',
            borderBottom: `1px solid ${colors.border}`,
            boxShadow: colors.cardShadow
          }}
        >
          <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {[
              { label: 'How it works', action: () => scrollToSection('how-it-works') },
              { label: 'Testimonials', action: () => scrollToSection('testimonials') },
              { label: 'Pricing', action: () => scrollToSection('pricing') },
              { label: 'Log in', action: () => navigate('/app/login') }
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.action)}
                className="text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-gray-50"
                style={{ color: colors.text }}
              >
                {item.label}
              </button>
            ))}
            
            <button
              onClick={() => handleNavClick(() => navigate('/signup'))}
              className="mx-4 mt-2 py-3 rounded-lg text-base font-semibold transition-all duration-200 hover:opacity-90"
              style={{
                backgroundColor: colors.primary,
                color: '#FFFFFF'
              }}
            >
              Sign up
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-16 sm:pt-20">
        {/* Hero Section - Single Meal Plan Mockup (Angled) */}
        <section 
          className="relative overflow-hidden"
          style={{ backgroundColor: colors.bgHero }}
        >
          {/* Soft radial glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full"
              style={{ 
                background: `radial-gradient(ellipse, ${colors.lightAccent} 0%, transparent 65%)`,
                opacity: heroLoaded ? 0.5 : 0,
                transition: 'opacity 1.2s ease-out',
                filter: 'blur(80px)'
              }}
            />
          </div>
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: Text Content */}
              <div className="text-center lg:text-left">
                <h1 
                  className="text-4xl sm:text-5xl lg:text-5xl font-semibold tracking-tight leading-[1.12] transition-all duration-1000 ease-out"
                  style={{
                    color: colors.text,
                    opacity: heroLoaded ? 1 : 0,
                    transform: heroLoaded ? 'translateY(0)' : 'translateY(16px)'
                  }}
                >
                  A meal plan you don&apos;t
                  <br />
                  have to think about.
                </h1>
                
                <p 
                  className="mt-5 text-lg sm:text-xl max-w-xl mx-auto lg:mx-0 transition-all duration-1000 ease-out"
                  style={{
                    color: colors.text,
                    opacity: heroLoaded ? 1 : 0,
                    transform: heroLoaded ? 'translateY(0)' : 'translateY(12px)',
                    transitionDelay: '150ms',
                    lineHeight: '1.8'
                  }}
                >
                  Lose weight without tracking, counting, or deciding what to eat.
                </p>
                
                {/* CTAs */}
                <div 
                  className="mt-8 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 transition-all duration-1000 ease-out"
                  style={{
                    opacity: heroLoaded ? 1 : 0,
                    transform: heroLoaded ? 'translateY(0)' : 'translateY(12px)',
                    transitionDelay: '300ms'
                  }}
                >
                  <Button 
                    onClick={handleSubscribe}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-8 h-12 rounded-xl text-base font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                  >
                    Try It Free for 14 Days
                  </Button>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-sm font-medium transition-all duration-200 hover:text-[#3B82F6]"
                    style={{ color: colors.textSecondary }}
                  >
                    See how it works ↓
                  </button>
                </div>
                
                <p 
                  className="mt-3 text-sm transition-all duration-1000 ease-out text-center lg:text-left"
                  style={{
                    color: colors.text,
                    opacity: heroLoaded ? 1 : 0,
                    transitionDelay: '400ms'
                  }}
                >
                  No card required. Cancel anytime.
                </p>
                
                <p 
                  className="mt-2 text-xs transition-all duration-1000 ease-out text-center lg:text-left"
                  style={{
                    color: colors.text,
                    opacity: heroLoaded ? 1 : 0,
                    transitionDelay: '450ms'
                  }}
                >
                  Real coach reviews and adjusts your plan every week.
                </p>
                
                <p 
                  className="mt-3 text-xs transition-all duration-1000 ease-out text-center lg:text-left"
                  style={{
                    color: colors.text,
                    opacity: heroLoaded ? 1 : 0,
                    transitionDelay: '500ms'
                  }}
                >
                  <span className="italic">"Lost 7kg without counting a single calorie."</span> — Conny, office worker
                </p>
              </div>
              
              {/* Right: Hero Phone Mockup - Angled & Large */}
              <div 
                className="flex flex-col items-center transition-all duration-1000 ease-out"
                style={{
                  opacity: heroLoaded ? 1 : 0,
                  transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)',
                  transitionDelay: '400ms'
                }}
              >
                <PhoneMockup 
                  src="/mockups/herosection_mockup.webp" 
                  alt="Today's meal plan with daily calories and breakfast, lunch, dinner"
                  size="lg"
                  rotate={3}
                  glowColor="rgba(59, 130, 246, 0.2)"
                />
                <p 
                  className="mt-4 text-sm text-center w-full"
                  style={{ color: colors.textMuted }}
                >
                  Calories and portions are already worked out for you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What MealGuide Does */}
        <section style={{ backgroundColor: colors.bgWhite }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <Animated>
              <div className="max-w-lg mx-auto text-center">
                <h2 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
                  style={{ color: colors.text }}
                >
                  Stop deciding. <br /> Start losing weight.
                </h2>
                
                <p 
                  className="mt-6 text-base sm:text-lg leading-relaxed"
                  style={{ color: colors.text }}
                >
                  MealGuide gives you a full meal plan with portions already worked out. <br />You follow it, log your weight, and check in weekly.<br /> A real coach adjusts your plan every Friday based on your progress.
                </p>
              </div>
            </Animated>
            
            <Animated delay={100}>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
                {[
                  "Real meals you'll actually want to eat",
                  'Portions already decided for you',
                  "Don't like something? Swap it instantly",
                  'Your coach adjusts the plan every Friday'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span 
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                      style={{ backgroundColor: colors.lightAccent }}
                    >
                      <Check className="w-3 h-3" style={{ color: colors.primary }} strokeWidth={3} />
                    </span>
                    <span className="text-sm sm:text-base" style={{ color: colors.text }}>{item}</span>
                  </li>
                ))}
              </ul>
            </Animated>
          </div>
        </section>

        {/* Recipe Section - Single Screenshot */}
        <section style={{ backgroundColor: colors.bgLight }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <Animated delay={100} className="flex justify-center">
              <PhoneMockup 
                src="/mockups/recipefeatures_mockup.webp" 
                alt="Recipe card with ingredients and portions"
                size="lg"
                rotate={0}
                glowColor="rgba(34, 197, 94, 0.15)"
              />
            </Animated>
            
            <Animated delay={250}>
              <p 
                className="mt-8 text-center text-base font-medium"
                style={{ color: colors.textSecondary }}
              >
                Clear portions, no guessing.
              </p>
            </Animated>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" style={{ backgroundColor: colors.bgWhite }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <Animated>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-center tracking-tight mb-10"
                style={{ color: colors.text }}
              >
                How it works
              </h2>
            </Animated>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  step: '1',
                  title: 'Set your targets',
                  description: 'Share your goals and food preferences during onboarding.'
                },
                {
                  step: '2',
                  title: 'Follow the plan',
                  description: 'Cook the recipes, eat the portions, and log your weight. Your coach handles the rest.'
                },
                {
                  step: '3',
                  title: 'Check in weekly',
                  description: 'Complete a short weekly check-in. Your meal plan is adjusted based on your progress.'
                }
              ].map((item, index) => (
                <Animated key={index} delay={index * 80}>
                  <div 
                    className="rounded-2xl p-6 h-full transition-all duration-300 hover:-translate-y-1"
                    style={{ 
                      backgroundColor: colors.bgWhite,
                      boxShadow: colors.cardShadow,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: colors.lightAccent }}
                    >
                      <span className="font-semibold text-sm" style={{ color: colors.primary }}>{item.step}</span>
                    </div>
                    <h3 
                      className="text-base sm:text-lg font-medium mb-2"
                      style={{ color: colors.text }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ color: colors.textSecondary }}
                    >
                      {item.description}
                    </p>
                  </div>
                </Animated>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section - Tracking Apps vs MealGuide */}
        <section style={{ backgroundColor: colors.bgLight }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <Animated>
              <p 
                className="text-center text-base sm:text-lg mb-8"
                style={{ color: colors.textSecondary }}
              >
                Most apps give you tools. MealGuide gives you structure.
              </p>
              <div 
                className="rounded-2xl overflow-hidden"
                style={{ 
                  backgroundColor: colors.bgWhite,
                  boxShadow: colors.cardShadow,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Left Column - Tracking Apps */}
                  <div className="p-6 sm:p-8 md:border-r" style={{ borderColor: colors.border }}>
                    {/* Icon and Title */}
                    <div className="flex items-center gap-3 mb-6">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: '#F1F5F9' }}
                      >
                        {/* List/Log icon */}
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#64748B" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <line x1="8" y1="6" x2="21" y2="6" />
                          <line x1="8" y1="12" x2="21" y2="12" />
                          <line x1="8" y1="18" x2="21" y2="18" />
                          <line x1="3" y1="6" x2="3.01" y2="6" />
                          <line x1="3" y1="12" x2="3.01" y2="12" />
                          <line x1="3" y1="18" x2="3.01" y2="18" />
                        </svg>
                      </div>
                      <h3 
                        className="text-lg font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Tracking apps
                      </h3>
                    </div>
                    
                    {/* Points */}
                    <ul className="space-y-4">
                      {[
                        'Log every calorie',
                        'Decide portions yourself',
                        'Constant decisions',
                        'Adjustments without context'
                      ].map((item, index) => (
                        <li 
                          key={index}
                          className="flex items-start gap-3 text-sm sm:text-base"
                          style={{ color: colors.text }}
                        >
                          <span 
                            className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2"
                            style={{ backgroundColor: colors.textMuted }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Footer line */}
                    <p 
                      className="mt-8 text-sm font-medium"
                      style={{ color: colors.textMuted }}
                    >
                      More thinking. More decisions.
                    </p>
                  </div>
                  
                  {/* Right Column - MealGuide */}
                  <div className="p-6 sm:p-8 border-t md:border-t-0" style={{ borderColor: colors.border }}>
                    {/* Icon and Title */}
                    <div className="flex items-center gap-3 mb-6">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: colors.lightAccent }}
                      >
                        {/* Calendar with checkmark icon */}
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#1A61E8" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                          <path d="M9 16l2 2 4-4" />
                        </svg>
                      </div>
                      <h3 
                        className="text-lg font-semibold"
                        style={{ color: '#1A61E8' }}
                      >
                        MealGuide
                      </h3>
                    </div>
                    
                    {/* Points */}
                    <ul className="space-y-4">
                      {[
                        'One structured meal plan',
                        'Portions already worked out',
                        'Weekly review and adjustment',
                        'Eat first, think less'
                      ].map((item, index) => (
                        <li 
                          key={index}
                          className="flex items-start gap-3 text-sm sm:text-base"
                          style={{ color: colors.text }}
                        >
                          <span 
                            className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2"
                            style={{ backgroundColor: '#1A61E8' }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Footer line */}
                    <p 
                      className="mt-8 text-sm font-medium"
                      style={{ color: '#1A61E8' }}
                    >
                      Structure without the mental load.
                    </p>
                  </div>
                </div>
              </div>
            </Animated>
          </div>
        </section>

        {/* Progress Section - Centered Large Card */}
        <section style={{ backgroundColor: colors.bgLight }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <Animated>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-center tracking-tight mb-4"
                style={{ color: colors.text }}
              >
                See your results
              </h2>
              <p 
                className="text-center text-base sm:text-lg max-w-lg mx-auto mb-12"
                style={{ color: colors.textSecondary }}
              >
                Just log your weight. Your coach handles the rest.
                
              </p>
            </Animated>
            
            <Animated delay={150} className="flex justify-center">
              <PhoneMockup 
                src="/mockups/progresstracking_mockup.webp" 
                alt="Weight progress showing starting weight, current weight, and goal weight"
                size="lg"
                rotate={0}
                glowColor="rgba(139, 92, 246, 0.18)"
              />
            </Animated>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" style={{ backgroundColor: colors.bgWhite }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <Animated>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-center tracking-tight mb-12"
                style={{ color: colors.text }}
              >
                Real results from real people
              </h2>
            </Animated>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: 'Conny',
                  context: 'Office worker, remote work',
                  quote: 'What helped most was having portions already decided. Working from home meant I was moving less, and this made it easier to stay consistent without overthinking food.',
                  image: '/testimonials/conny.png',
                  weightLoss: '-7 kg'
                },
                {
                  name: 'Shkelqim',
                  context: 'Clothing store manager, restaurant worker, parent',
                  quote: 'With two jobs and kids, I didn\'t have time to plan meals properly. This removed the daily stress of deciding what to eat and helped me stay on track even on busy days.',
                  image: '/testimonials/shkelqim.png',
                  weightLoss: '-6.5 kg'
                },
                {
                  name: 'Göran',
                  context: 'Project manager, family man',
                  quote: 'I didn\'t have the time or energy to figure out what and how much to eat. Following a clear plan made it easier to stick to things and see progress over time.',
                  image: '/testimonials/goran.png',
                  weightLoss: '-9 kg'
                }
              ].map((testimonial, index) => (
                <Animated key={index} delay={index * 100}>
                  <div 
                    className="rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1"
                    style={{ 
                      backgroundColor: colors.bgWhite,
                      boxShadow: colors.cardShadow,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    {/* Before/After Image */}
                    <div className="relative">
                      <img 
                        src={testimonial.image} 
                        alt={`${testimonial.name}'s transformation`}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Weight Loss Number */}
                    <p 
                      className="text-center font-bold text-xl py-3"
                      style={{ color: colors.primary }}
                    >
                      {testimonial.weightLoss}
                    </p>
                    
                    {/* Content */}
                    <div className="px-5 pb-5 flex flex-col flex-grow">
                      {/* Quote */}
                      <p 
                        className="text-base sm:text-[17px] leading-relaxed mb-4 flex-grow"
                        style={{ color: colors.text }}
                      >
                        "{testimonial.quote}"
                      </p>
                      
                      {/* Name & Context */}
                      <div className="pt-3" style={{ borderTop: `1px solid ${colors.border}` }}>
                        <p 
                          className="font-semibold text-base"
                          style={{ color: colors.text }}
                        >
                          {testimonial.name}
                        </p>
                        <p 
                          className="text-xs mt-0.5"
                          style={{ color: colors.textMuted }}
                        >
                          {testimonial.context}
                        </p>
                      </div>
                    </div>
                  </div>
                </Animated>
              ))}
            </div>

            <Animated delay={200}>
              <div className="mt-12 max-w-md mx-auto px-4 sm:px-0">
                <Button
                  onClick={handleSubscribe}
                  data-testid="testimonials-cta-button"
                  className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-14 rounded-xl text-base font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                >
                  Try It Free for 14 Days
                </Button>
                <p
                  className="mt-3 text-sm text-center"
                  style={{ color: colors.text }}
                >
                  No card required. Cancel anytime.
                </p>
              </div>
            </Animated>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" style={{ backgroundColor: colors.bgLight }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <Animated>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-center tracking-tight mb-10"
                style={{ color: colors.text }}
              >
                Pricing
              </h2>
            </Animated>
            
            <Animated delay={100}>
              <div className="max-w-sm mx-auto">
                <div 
                  className="rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-1"
                  style={{ 
                    backgroundColor: colors.bgWhite,
                    boxShadow: colors.cardShadow,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <p 
                    className="text-sm mb-5"
                    style={{ color: colors.textSecondary }}
                  >
                    Start with a free trial.
                  </p>
                  
                  <div className="space-y-1">
                    <p className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ color: colors.text }}>
                      14 days free
                    </p>
                    <p className="text-base" style={{ color: colors.textSecondary }}>
                      then £27/month if you continue
                    </p>
                    <p className="text-sm" style={{ color: colors.text }}>
                      No Card Required To Start
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleSubscribe}
                    className="mt-6 w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-12 rounded-xl text-base font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                  >
                    Start free trial
                  </Button>
                  
                  <p 
                    className="mt-4 text-sm"
                    style={{ color: colors.text }}
                  >
                    Cancel anytime.
                  </p>
                  <p 
                    className="mt-1 text-sm font-medium"
                    style={{ color: colors.text }}
                  >
                  Full refund if you're not happy after your 1st month.
                  </p>
                </div>
              </div>
            </Animated>
          </div>
        </section>

        {/* Closing */}
        <section style={{ backgroundColor: colors.bgLight }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
            <Animated>
              <div className="max-w-lg mx-auto text-center">
                <p 
                  className="text-lg sm:text-xl leading-relaxed"
                  style={{ color: colors.textSecondary }}
                >
                  If a steady plan sounds easier than constant decisions, MealGuide is for you.
                </p>
              </div>
            </Animated>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer 
        className="py-8"
        style={{ backgroundColor: colors.bgWhite, borderTop: `1px solid ${colors.border}` }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-3">
            {/* Policy Links */}
            <div 
              className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-sm"
              style={{ color: colors.textSecondary }}
            >
              <a 
                href="/privacy" 
                className="transition-colors duration-200 hover:text-[#3B82F6]"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms" 
                className="transition-colors duration-200 hover:text-[#3B82F6]"
              >
                Terms of Service
              </a>
              <a 
                href="/refund" 
                className="transition-colors duration-200 hover:text-[#3B82F6]"
              >
                Refund Policy
              </a>
            </div>
            
            {/* Contact Info */}
            <div 
              className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm"
              style={{ color: colors.textSecondary }}
            >
              <span>getmealguide.com</span>
              <span className="hidden sm:inline" style={{ color: colors.border }}>·</span>
              <a 
                href="mailto:support@getmealguide.com" 
                className="transition-colors duration-200 hover:text-[#3B82F6]"
              >
                support@getmealguide.com
              </a>
            </div>
            
            <p 
              className="mt-2 text-xs text-center max-w-lg leading-relaxed"
              style={{ color: colors.textMuted }}
            >
              MealGuide provides general nutrition guidance and meal planning tools.
              It is not a substitute for medical advice.
              If you have a medical condition, are pregnant, or have specific dietary needs, consult a qualified healthcare professional before use.
            </p>
            
            <p 
              className="mt-2 text-xs text-center max-w-lg leading-relaxed"
              style={{ color: colors.textMuted }}
            >
              Results vary depending on individual adherence, lifestyle, and starting point. MealGuide provides nutrition guidance and meal planning tools and does not guarantee specific weight loss outcomes.
            </p>
            
            {/* Copyright */}
            <p 
              className="mt-4 text-sm font-medium"
              style={{ color: colors.textSecondary }}
            >
              © 2026 MealGuide. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
